import type { PolicyLoanSummary } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { addMonthsToKey, currentMonthKey, formatMonthKey } from '@/lib/months';

/** Loan rules (the server enforces them; the UI uses them for hints and the calculator). */
export const LOAN_MAX = 25_000;
export const LOAN_MIN = 1_000;
export const LOAN_MIN_PRINCIPAL = 1_000;
export const LOAN_RATE = 0.09; // 9% per year on the outstanding principal
export const LOAN_MIN_PAID_MONTHS = 6;

/** Monthly loan interest on the outstanding principal, rounded half up to whole rupees. */
export const getLoanInterest = (outstanding: number): number =>
  Math.round((outstanding * LOAN_RATE) / 12);

export const getLoanAmountError = (amount: number): string => {
  if (!amount) return '';
  if (amount > LOAN_MAX) return `Loan amount can be up to ${formatCurrency(LOAN_MAX)} only`;
  if (amount < LOAN_MIN) return `Minimum loan amount is ${formatCurrency(LOAN_MIN)}`;
  return '';
};

/**
 * Admin calculator for a new loan: what the first month costs and how long the
 * loan takes at a chosen monthly principal. Nothing here is sent to the API.
 */
export const getLoanPlan = (amount: number, monthlyPrincipal: number) => {
  const firstInterest = getLoanInterest(amount);
  const months = Math.ceil(amount / monthlyPrincipal);

  return {
    firstInterest,
    firstTotal: monthlyPrincipal + firstInterest,
    months,
    closesIn: addMonthsToKey(currentMonthKey(), months),
  };
};

/** Short text for a policy that has no open loan (table and drawers). */
export const getLoanEligibilityText = (loan: PolicyLoanSummary): string => {
  switch (loan.eligibility) {
    case 'eligible':
      return `Eligible · Up to ${formatCurrency(LOAN_MAX)}`;
    case 'waiting':
      return `Available from ${formatMonthKey(loan.availableFrom)}`;
    case 'policy_closed':
      return 'Policy closed';
    case 'not_eligible':
      return `From 7th month (${LOAN_MIN_PAID_MONTHS - (loan.monthsNeeded ?? 0)}/${LOAN_MIN_PAID_MONTHS} paid)`;
    default:
      return '--';
  }
};

/** Loan numbers are "L" + 3-digit sequence + policy number: L001P2701 → P2701. */
export const policyNoFromLoanNo = (loanNo: string): string => loanNo.slice(4);
